<template>
  <div>
    <h1 v-bg-typography="'h1'">User tests</h1>
    <p v-bg-typography="'body-m'">
      This is the place where you can create a new test, see the status of your
      requested test and check out the done tests.
    </p>
    <div>
      <div class="search-filters">
        <bg-field
          id="searchfield"
          class="search-input"
          label="Search"
          v-model="searchString"
          name="search"
        >
          <bg-input
            name="search"
            placeholder="Type in a test name or requester"
            v-model="searchString"
            height="46px"
          />
        </bg-field>
        <bg-field
          class="field"
          label="Platform"
          style="margin-right: 25px; max-width: 200px;"
        >
          <bg-select
            @change="selectDevice"
            :options="deviceOptions"
            :value="device"
            label="text"
          ></bg-select>
        </bg-field>
        <bg-field label="Status">
          <div class="filter-container">
            <bg-filter-bubble
              class="filter"
              v-for="filter in filters"
              :key="filter"
              :text="filter | BgKebabReadable"
              :variant="filter | BgKebab"
              :active="activeFilters.includes(filter)"
              @click.native="toggleFilter($event, filter)"
            />
          </div>
        </bg-field>
      </div>

      <div class="all-tests">
        <transition-group
          name="animatestaggered-fade"
          tag="div"
          class="all-tests"
          v-bind:css="false"
          v-on:before-enter="beforeEnter"
          v-on:enter="enter"
          v-on:leave="leave"
        >
          <div
            v-for="(test, index) in testList"
            :key="test.id"
            class="test"
            v-bind:data-index="index"
          >
            <router-link :to="generateTestLink(test)">
              <bg-splash-card
                :status="test.state"
                :statusText="test.state | BgKebabReadable"
                :byline="test.byline"
                :date="test.updated_at | BgDayMonthYear"
                :title="test.name"
                :platform="test.platform"
                :pricingPlan="test.test_type"
                :name="
                  test.requester
                    ? test.requester.profile.first_name +
                      ' ' +
                      test.requester.profile.last_name
                    : ''
                "
              />
            </router-link>
          </div>
        </transition-group>
      </div>
      <bg-loader :dark="true" v-if="$store.state.tests.loading"></bg-loader>
    </div>
  </div>
</template>

<style scoped>
.search-filters {
  display: flex;
}

.search-filters .bg-field {
  margin-bottom: 0px;
}

#searchfield {
  max-width: 307px;
  margin-right: 30px;
}

.filter {
  margin-right: 12px;
}
.filter-container {
  display: flex;
  flex-direction: row;
  height: 46px;
  align-items: center;
}
.search-input {
  max-width: 300px;
  margin-right: 140px;
}
.all-tests {
  display: flex;
  flex-wrap: wrap;
  min-height: 100px;
  position: relative;
}

.all-tests .test {
  margin: 15px;
}

.all-tests .test:nth-child(3n + 1) {
  margin-left: 0;
}

.test {
  cursor: pointer;
}
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import BgField from "@/components/bg-field";
import BgInput from "@/components/bg-input";
import BgFilterBubble from "@/components/bg-filter-bubble";
import BgSplashCard from "@/components/bg-splash-card";
import BgLoader from "@/components/bg-loader";
import BgKebab from "@/filters/bg-kebab";
import BgKebabReadable from "@/filters/bg-kebab-readable";
import BgDayMonthYear from "@/filters/bg-day-month-year";
import Velocity from "velocity-animate";
import { mapState } from "vuex";
import BgSelect from "@/components/bg-select";
export default {
  name: "tests",
  components: {
    BgField,
    BgSelect,
    BgSplashCard,
    BgFilterBubble,
    BgInput,
    BgLoader,
  },
  filters: {
    BgDayMonthYear,
    BgKebab,
    BgKebabReadable,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      activeFilters: [],
      animate: true,
      device: "All",
      deviceOptions: ["All", "Desktop", "Mobile", "Video"],
      filters: ["draft", "requested", "in-progress", "done"],
      isLoading: false,
      searchString: "",
      tests: [],
    };
  },
  watch: {
    searchString(newVal) {
      if (newVal !== "") {
        this.animate = false;
      }
    }
  },
  computed: {
    ...mapState({
      testsState: (state) => state.tests.tests,
    }),
    testList() {
      const filteredBySearch =
        this.searchString !== ""
          ? this.testsState.filter((t) => {
              if (!t || typeof t !== "object") return false;

              const testName = typeof t.name === "string" ? t.name : "";

              const fullName =
                t.requester && t.requester.profile
                  ? `${t.requester.profile.first_name || ""} ${t.requester.profile.last_name || ""}`
                  : "";

              const search = this.searchString.toLowerCase();
              return (
                testName.toLowerCase().includes(search) ||
                fullName.toLowerCase().includes(search)
              );
            })
          : this.testsState;

      if (this.activeFilters.length === 0 && this.device === "All") {
        return filteredBySearch;
      } else {
        return filteredBySearch.filter((d) => {
          let deviceCheck = true;
          let statusCheck = true;

          if (this.device !== "All") {
            deviceCheck = d.platform === this.device.toLowerCase();
          }

          if (this.activeFilters.length > 0) {
            statusCheck = this.activeFilters.includes(d.state);
          }

          return statusCheck && deviceCheck;
        });
      }
    },

  },
  methods: {
    toggleFilter(event, filter) {
      this.animate = false;
      if (this.activeFilters.includes(filter)) {
        this.activeFilters = this.activeFilters.filter((f) => f !== filter);
      } else {
        this.activeFilters.push(filter);
      }
    },
    generateTestLink(test) {
      let base = "test";
      if (test.platform === "video") {
        base = "video-test";
      }

      let editBase = test.platform === "video" ? "video" : "web";
      let testRoute = "";
      if (test.state === "done") {
        testRoute = `/${base}/${test.id}/`;
        testRoute += editBase === "video" ? "summary/" : "overview/";
      } else {
        testRoute = `/create-test/${editBase}/${test.id}`;
      }
      return testRoute;
    },
    beforeEnter: function(el) {
      if (this.animate) {
        el.style.opacity = 0;
      }
    },
    enter: function(el, done) {
      if (!this.animate) {
        done();
      } else {
        var delay = el.dataset.index * 100;
        setTimeout(function() {
          Velocity(el, { opacity: 1 }, { complete: done });
        }, delay);
      }
    },
    leave: function(el, done) {
      if (!this.animate) {
        done();
      } else {
        var delay = el.dataset.index * 100;
        setTimeout(function() {
          Velocity(el, { opacity: 0 }, { complete: done });
        }, delay);
      }
    },
    selectDevice(val) {
      this.device = val;
    },
  },
};
</script>
